import { useMemo } from "react";
import { useIntl } from "react-intl";
import { IBudget } from "../../../models/budgetV2";
import { ITruckerPayPlanData } from "../../../models/truckerPayPlan";
import { formatNumberToCurrency } from "../../../utils/stringUtils";

interface IBudgetSummaryProps {
    persona: string;
    budget?: IBudget | null;
    truckerPayPlan?: ITruckerPayPlanData | null;
}

export const BudgetSummary = (props: IBudgetSummaryProps) => {
    const intl = useIntl();

    const { persona, budget, truckerPayPlan } = props;

    const summary = useMemo(() => {
        if (!budget?.versionData?.services) {
            return {
                subtotal: 0,
                discounts: 0,
                subscriptionBalanceApplied: 0,
                subscriptionBalanceAvailable: 0,
                subscriptionTotalApproved: 0,
                totalToPay: 0,
                counterProposalValue: 0,
            };
        }

        let totalSubtotal = 0;
        let totalDiscounts = 0;
        let subscriptionTotalApproved = 0;

        for (const service of budget.versionData.services) {
            let serviceSubtotal = 0;
            const serviceItems =
                (service.items?.length ? service.items : undefined)
                ?? (service.serviceItems?.length ? service.serviceItems : undefined)
                ?? service.items
                ?? service.serviceItems
                ?? [];

            for (const item of serviceItems) {
                if (item.removed) continue;

                const itemValue = item.value || 0;
                let clean = String(item.quantity || '0').replace(/[^\d.,]/g, '');
                clean = clean.replace(',', '.');
                const parsedQuantity = Number.parseFloat(clean);
                const quantity = Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;

                if (quantity <= 0) continue;

                const discountStr = String(item.discount ?? '0');
                let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, '');
                cleanDiscountStr = cleanDiscountStr.replaceAll(',', '.');
                const itemDiscountPercent = Number.parseFloat(cleanDiscountStr) || 0;

                const grossValue = itemValue * quantity;
                const itemDiscount = (grossValue * itemDiscountPercent) / 100;
                const itemSubtotal = grossValue - itemDiscount;

                if (item.isCovered === true) {
                    subscriptionTotalApproved += itemSubtotal;
                    continue;
                }

                serviceSubtotal += itemSubtotal;
            }

            const serviceDiscountPercent =
                (service.percentages?.volkstok || 0) +
                (service.percentages?.dealership || 0) +
                (service.percentages?.warranty || 0);

            const serviceDiscount = (serviceSubtotal * serviceDiscountPercent) / 100;

            totalSubtotal += serviceSubtotal;
            totalDiscounts += serviceDiscount;
        }

        const netAfterServiceDiscounts = totalSubtotal - totalDiscounts;
        const balanceToApply =
            budget.subscriptionBalanceApplied ??
            Math.min(truckerPayPlan?.availableCoverageBalance ?? 0, netAfterServiceDiscounts);

        const totalToPay = Math.max(0, netAfterServiceDiscounts - balanceToApply);

        return {
            subtotal: totalSubtotal,
            discounts: totalDiscounts,
            totalToPay,
            subscriptionBalanceApplied: balanceToApply,
            subscriptionBalanceAvailable: truckerPayPlan?.availableCoverageBalance ?? 0,
            subscriptionTotalApproved,
            counterProposalValue: budget.versionData.counterProposalValue,
        };
    }, [budget, truckerPayPlan]);

    return (
        <div className={`mb-6 px-6`}>
            <div className="flex items-center justify-between border border-gray-300 rounded-md p-4">
                {!(persona === 'fleet-manager' && !truckerPayPlan) ? (
                    <div className='flex items-center gap-5 bg-[#F5F8FB] p-5'>
                        <span className='rioglyph rioglyph-info-sign text-[#4B80A699] text-3xl'></span>
                        <p className='text-xl text-[#2A3740]'>{intl.formatMessage({ id: 'intl-msg:budgets.willBeIssuedABilletWithTheAmountToBePaid' })}</p>
                    </div>
                ) : <div />}

                <div className="flex flex-col">
                    <div className="flex flex-row items-center justify-end gap-5 text-[#2A374099] pr-2 text-size-20 text-uppercase text-medium">
                        <span>{intl.formatMessage({ id: 'intl-msg:budgets.subtotal' })}:</span>
                        <span>{formatNumberToCurrency((summary.subtotal ?? 0) + (summary.subscriptionTotalApproved ?? 0))}</span>
                    </div>
                    {summary.discounts ? (
                        <div className="flex flex-row items-center justify-end gap-5 text-size-20 text-uppercase text-medium">
                            <span className="text-green-600">{intl.formatMessage({ id: 'intl-msg:budgets.discounts' })}:</span>
                            <span className="font-bold text-green-600">{formatNumberToCurrency(summary.discounts)}</span>
                        </div>
                    ) : null}
                    {summary.subscriptionTotalApproved ? <div className="flex flex-row items-center justify-end gap-5">
                        <span className="text-green-600">{intl.formatMessage({ id: 'intl-msg:budgets.approvedMaintenancePlanValue' })}:</span>
                        <span className="font-bold text-green-600">{formatNumberToCurrency(summary.subscriptionTotalApproved)}</span>
                    </div> : null}
                    {!(persona === 'fleet-manager' && !truckerPayPlan) && summary.subscriptionBalanceApplied ? <div className="flex flex-row items-center justify-end gap-5 text-[#5CB85C] text-size-20 text-uppercase text-medium">
                        <span>{intl.formatMessage({ id: 'intl-msg:budgets.usedBalance' })}:</span>
                        <span>{formatNumberToCurrency(summary.subscriptionBalanceApplied)}</span>
                    </div> : null}
                    <div className={`flex flex-row items-center justify-end gap-5 text-[#2A3740] pr-2 text-size-18 text-size-20 text-uppercase text-medium ${budget?.versionData?.counterProposalApprovedAt ? 'line-through' : ''}`}>
                        <span>{intl.formatMessage({ id: 'intl-msg:budgets.toBePaid' })}:</span>
                        <span>{formatNumberToCurrency(summary.totalToPay)}</span>
                    </div>
                    {budget?.versionData?.counterProposalApprovedAt && (
                        <div className="flex flex-row items-center justify-end gap-5">
                            <span className="font-bold text-size-16">{intl.formatMessage({ id: 'intl-msg:budgets.toBePaid' })}:</span>
                            <span className="font-bold text-size-16">{formatNumberToCurrency(budget?.versionData?.counterProposalValue || 0)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};